/**
 * NotificationEditorPage — `/service-desk/notifications`.
 *
 * Slice B10: Service-desk-specific MDA outbound notification template editor.
 * NOT to be confused with the general /notifications/* surface (service-notification).
 * Drafts are persisted in localStorage. The backend notification-template persistence
 * endpoint will be added when service-notification ships SDA support (separate slice).
 */
import { type JSX, useEffect, useState } from 'react';
import { Check, Mail, MessageSquare, Smartphone } from 'lucide-react';

import { PageHeader } from '../shared/StaffUI';

const STORAGE_KEY = 'upmp.service-desk.notification-draft';

const SAMPLE_VARS = ['{{customer_name}}', '{{tracking_number}}', '{{reference_number}}', '{{status}}'];

const DEFAULT_BODIES: Record<'SMS' | 'Email' | 'Push', string> = {
  SMS: 'Dear {{customer_name}}, your service request {{reference_number}} status is now {{status}}. — NIPOST',
  Email:
    'Dear {{customer_name}},\n\nYour government-service request {{reference_number}} is now {{status}}.\n\nThank you for using NIPOST.',
  Push: 'Your request {{reference_number}} is now {{status}}.',
};

export function NotificationEditorPage(): JSX.Element {
  const [channel, setChannel] = useState<'SMS' | 'Email' | 'Push'>('SMS');
  const [subject, setSubject] = useState('Your NIPOST government service update');
  const [body, setBody] = useState(DEFAULT_BODIES.SMS);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as { channel?: 'SMS' | 'Email' | 'Push'; subject?: string; body?: string };
        if (parsed.channel) setChannel(parsed.channel);
        if (typeof parsed.subject === 'string') setSubject(parsed.subject);
        if (typeof parsed.body === 'string') setBody(parsed.body);
      }
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ channel, subject, body }));
    } catch {
      // ignore
    }
  }, [channel, subject, body]);

  function changeChannel(next: 'SMS' | 'Email' | 'Push'): void {
    setChannel(next);
    setBody(DEFAULT_BODIES[next]);
  }

  function handleSave(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ channel, subject, body }));
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {
      // ignore
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Notification template editor"
        description="Service-desk MDA outbound templates — per channel (SMS / Email / Push)."
        actions={
          <button
            type="button"
            onClick={handleSave}
            className="px-3 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg inline-flex items-center gap-2"
          >
            {saved ? <><Check className="w-4 h-4" /> Saved</> : 'Save template'}
          </button>
        }
      />

      <div className="bg-white border border-border rounded-xl p-5 space-y-4">
        <div className="flex items-center gap-2">
          {(['SMS', 'Email', 'Push'] as const).map((ch) => {
            const Icon = ch === 'SMS' ? MessageSquare : ch === 'Email' ? Mail : Smartphone;
            return (
              <button
                key={ch}
                type="button"
                onClick={() => changeChannel(ch)}
                className={`px-3 py-2 rounded-lg border text-sm font-medium inline-flex items-center gap-2 ${
                  channel === ch
                    ? 'border-nipost-green bg-nipost-green-light text-nipost-green'
                    : 'border-border'
                }`}
              >
                <Icon className="w-4 h-4" /> {ch}
              </button>
            );
          })}
        </div>

        {channel === 'Email' ? (
          <div>
            <label className="block text-xs font-medium text-muted mb-1">Subject</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg text-sm"
            />
          </div>
        ) : null}

        <div>
          <label className="block text-xs font-medium text-muted mb-1">Body</label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={6}
            className="w-full px-3 py-2 border border-border rounded-lg text-sm font-mono"
          />
        </div>

        <div>
          <p className="text-xs font-medium text-muted mb-1">Variables</p>
          <div className="flex flex-wrap gap-2">
            {SAMPLE_VARS.map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setBody((cur) => `${cur}${v}`)}
                className="px-2 py-1 text-xs font-mono bg-gray-100 rounded hover:bg-gray-200"
              >
                {v}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
